import React, { useMemo } from 'react';

import { useDraggable } from '@dnd-kit/core';
import { Button } from '@smartshell/ui-kit';
import cn from 'classnames';

import {
  ArrowIcon,
  BarIcon,
  CloakroomIcon,
  KitchenIcon,
  LoungeIcon,
  ReceptionIcon,
  RestroomIcon,
  RotateIcon,
} from 'components';

import { DEFAULT_TEXT_STYLE } from '../../constants';
import { objectDragId } from '../../dragIds';
import { getBlockHeight, getBlockWidth } from '../../utils';
import s from './DraggableMapObject.module.scss';
import { IDraggableMapObjectProps } from './interfaces';

export const DraggableMapObject: React.FC<IDraggableMapObjectProps> = ({
  obj,
  isEdit,
  onRotate,
  onDelete,
  openTextModal,
  onMapObjectsChange,
  hasConflict,
  isSave,
  defaultTextStyle,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: objectDragId(obj.id),
    disabled: !isEdit,
  });

  const showText =
    obj.text ||
    obj.type === 'wall' ||
    obj.type === 'wall_corner' ||
    obj.type === 'wall_t' ||
    obj.type === 'wall_x';

  const textContentSingleLineStyle = useMemo(() => {
    const styleData = obj.textStyle || DEFAULT_TEXT_STYLE;
    const width = getBlockWidth(obj.text || '', styleData.fontSize);
    const height = getBlockHeight(obj.text || '', styleData.fontSize);
    let textAlign: 'left' | 'center' | 'right';

    switch (styleData.horizontalAlign) {
      case 'flex-end':
        textAlign = 'right';
        break;
      case 'center':
        textAlign = 'center';
        break;
      case 'flex-start':
      default:
        textAlign = 'left';
        break;
    }

    return {
      fontSize: `${styleData.fontSize}px`,
      justifyContent: styleData.horizontalAlign,
      color: styleData.color,
      alignItems: styleData.verticalAlign,
      width: `${width}px`,
      height: `${height}px`,
      textAlign,
    };
  }, [obj.textStyle, obj.text]);

  return (
    <div className={cn(s.container, hasConflict && s.conflict)}>
      {isEdit && !isSave && (
        <div className={cn(!showText && s.hover_small, s.hover)}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onRotate(obj.id);
            }}
            className={s.button}
          >
            <RotateIcon />
          </Button>

          {showText && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                openTextModal({
                  objectId: obj.id,
                  initialText: obj.text || '',
                  initialTextStyle: { ...(obj.textStyle || defaultTextStyle) },
                  onSave: (newText, textStyle) => {
                    if (newText === '' && obj.type === 'text') {
                      onDelete(obj.id);
                    }
                    onMapObjectsChange((prev) =>
                      prev.map((o) =>
                        o.id === obj.id
                          ? {
                              ...o,
                              text: newText || undefined,
                              textStyle: { ...(textStyle || defaultTextStyle) },
                            }
                          : o
                      )
                    );
                  },
                  onCancel: () => {
                    /*empty*/
                  },
                });
              }}
              className={s.button}
            >
              <svg width="16" height="16" viewBox="0 0 16 16">
                <path d="M8.66683 4V14H7.3335V4H3.3335V2.66667H12.6668V4H8.66683Z" />
              </svg>
            </Button>
          )}

          <Button
            onlyIcon
            onClick={(e) => {
              e.stopPropagation();
              onDelete(obj.id);
            }}
            className={s.button}
            iconName={'BucketIcon'}
          />
        </div>
      )}

      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        className={cn(
          s.mapObjectWrapper,
          isDragging && s.isDraggingWrapper,
          isEdit && !isSave && s.edit
        )}
        style={{
          cursor: isEdit && !isSave ? 'grab' : 'default',
        }}
      >
        <div
          className={cn(
            s.mapObject,
            s[obj.type],
            isDragging && s.isDragging,
            isEdit && !isSave && s.edit
          )}
          style={{
            transform:
              obj.rotation != null ? `rotate(${obj.rotation}deg)` : undefined,
          }}
        >
          {obj.text && (
            <div
              className={s.textContentSingleLine}
              style={textContentSingleLineStyle}
            >
              {obj.text}
            </div>
          )}

          {obj.type === 'wall' && <span className={s.wall}></span>}
          {obj.type === 'wall_corner' && (
            <span className={s.wall_corner}></span>
          )}
          {obj.type === 'wall_t' && <span className={s.wall_t}></span>}
          {obj.type === 'wall_x' && <span className={s.wall_x}></span>}
          {obj.type === 'pointer' && (
            <span className={s.arrow}>
              <ArrowIcon />
            </span>
          )}
          {obj.type === 'reception' && (
            <span className={s.center}>
              <ReceptionIcon />
            </span>
          )}
          {obj.type === 'cloakroom' && (
            <span className={s.center}>
              <CloakroomIcon />
            </span>
          )}
          {obj.type === 'restroom' && (
            <span className={s.center}>
              <RestroomIcon />
            </span>
          )}
          {obj.type === 'bar' && (
            <span className={s.center}>
              <BarIcon />
            </span>
          )}
          {obj.type === 'lounge' && (
            <span className={s.center}>
              <LoungeIcon />
            </span>
          )}
          {obj.type === 'kitchen' && (
            <span className={s.center}>
              <KitchenIcon />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
